/**
 * A list of one kind of item in the business's words (Classes, Messages, Screens...): status
 * first (the badge), then the name; filters for what needs doing; one primary action, "New
 * class". Used by the Library, Live & schedule (with its schedule view) and Your app (screens).
 */

import { useEffect, useMemo, useState } from "react";
import Button from "@web-ui/components/Button";
import DataTable from "@web-ui/components/DataTable";
import DataTableRow from "@web-ui/components/DataTableRow";
import Empty from "@web-ui/components/Empty";
import Field from "@web-ui/components/Field";
import FilterButtons from "@web-ui/components/FilterButtons";
import Modal from "@web-ui/components/Modal";
import Notice from "@web-ui/components/Notice";
import TableCard from "@web-ui/components/TableCard";
import { entities, type Entity, type EntityType } from "@/api/admin";
import { ApiError } from "@/api/client";
import { PageBar } from "@/components/page-bar";
import { StatusBadge } from "@/components/status-badge";
import { blankEntity } from "@/forms/blank";
import { uniqueId } from "@/lib/ids";
import { relative } from "@/lib/format";
import { go, href, itemRoute, type Route } from "@/lib/route";
import { isPending } from "@/publishing/status";
import { useStudio } from "@/shell/studio";
import { lower, noun, type Pack } from "@/vocabulary/vocabulary";
import { ScheduleGrid } from "./schedule";

type Show = "all" | "draft" | "pending" | "live";
const SHOWS: Show[] = ["all", "draft", "pending", "live"];

/** One line under the list's title: what this kind of item is, in plain words. */
function describe(type: EntityType, pack: Pack): string {
  const items = lower(noun(pack, "title", "many"));
  switch (type) {
    case "title": return `All your ${items}: each one a video in your app.`;
    case "series": return `${items[0]!.toUpperCase()}${items.slice(1)} grouped in order.`;
    case "liveEvent": return "What streams live, with its start time.";
    case "collection": return "Hand-picked sets you show as rows in the app.";
    case "person": return "The people your viewers see on items.";
    case "taxonomy": return "The lists viewers browse and filter by.";
    case "stage": return "Rooms, pitches or streams that run at the same time.";
    case "page": return "The screens of your app: the tabs in its menu, each with its rows.";
  }
}

export function ItemList({ route }: { route: Route }) {
  const { client, pack, canEdit, refresh } = useStudio();
  const type = route.type ?? "title";
  const [rows, setRows] = useState<Entity[] | null>(null);
  const [q, setQ] = useState("");
  const [error, setError] = useState("");
  const [view, setView] = useState<"list" | "schedule">("list");
  const show: Show = (SHOWS as string[]).includes(route.show ?? "") ? (route.show as Show) : "all";
  const creating = route.show === "new";
  const many = noun(pack, type, "many");
  const one = noun(pack, type);

  useEffect(() => {
    let live = true;
    setRows(null); setError("");
    entities.listFull(client, type)
      .then((r) => { if (live) setRows(r.entities); })
      .catch(() => { if (live) { setRows([]); setError(`The ${lower(many)} did not load. Reload the page to try again.`); } });
    return () => { live = false; };
  }, [client, type, many]);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (rows ?? [])
      .filter((r) => show === "all" || (show === "draft" ? r.status === "draft" : show === "pending" ? isPending(r.publishState) : r.publishState === "live" || r.publishState === "changed"))
      .filter((r) => !needle || r.label.toLowerCase().includes(needle) || r.id.toLowerCase().includes(needle))
      .sort((a, b) => b.updatedAt - a.updatedAt);
  }, [rows, q, show]);

  const brand = client.brand.id;
  const base = itemRoute(brand, type);
  const filters = [{ id: "all" as const, label: "All" }, { id: "draft" as const, label: "Drafts" }, { id: "pending" as const, label: "Not live yet" }, { id: "live" as const, label: "Live" }];
  const newLabel = `New ${lower(one)}`;

  return (
    <>
      <PageBar>
        {canEdit ? <Button variant="primary" type="button" icon="plus" onClick={() => go({ ...base, show: "new" })}>{newLabel}</Button> : null}
      </PageBar>
      {type === "liveEvent" ? (
        <div className="flex">
          <FilterButtons label="View" items={[{ id: "list" as const, label: "List" }, { id: "schedule" as const, label: "Schedule" }]} value={view} onChange={(next) => setView(next)} />
        </div>
      ) : null}
      {type === "liveEvent" && view === "schedule" ? <ScheduleGrid client={client} /> : (
        <TableCard
          title={many}
          description={describe(type, pack)}
          filters={<FilterButtons label="Show" items={filters} value={show} onChange={(id) => go({ ...base, ...(id === "all" ? {} : { show: id }) })} />}
          actions={<Field aria-label={`Search ${lower(many)}`} size="sm" placeholder={`Search ${lower(many)}`} value={q} onChange={(e) => setQ(e.target.value)} />}
          notices={error ? <Notice tone="critical">{error}</Notice> : undefined}
        >
          <DataTable
            label={many}
            loading={rows === null}
            isEmpty={shown.length === 0}
            empty={
              <Empty icon="layers"
                title={q || show !== "all" ? `No ${lower(many)} match` : `No ${lower(many)} yet`}
                description={q || show !== "all" ? "Try another search or filter." : canEdit ? `Add your first ${lower(one)}. It stays a draft until you show it in the app and publish.` : undefined}
                action={canEdit && !q && show === "all" ? <Button variant="secondary" size="sm" icon="plus" type="button" onClick={() => go({ ...base, show: "new" })}>{`Add your first ${lower(one)}`}</Button> : undefined} />
            }
            cellsThead={<><th>Status</th><th>Name</th><th className="max-md:hidden">Last saved</th></>}
          >
            {shown.map((r) => (
              <DataTableRow key={r.id}>
                <td><StatusBadge state={r.publishState} availability={r.data["availability"] as { from?: string; until?: string } | undefined} /></td>
                <td><a className="text-body-2 font-medium hover:underline focus-visible:underline" href={href(itemRoute(brand, type, r.id))}>{r.label || `(no name)`}</a></td>
                <td className="text-body-2 text-t-secondary max-md:hidden">{relative(r.updatedAt)}</td>
              </DataTableRow>
            ))}
          </DataTable>
        </TableCard>
      )}
      <NewItem open={creating && canEdit} type={type} taken={new Set((rows ?? []).map((r) => r.id))} onClose={() => go(base)} onCreated={async (id) => { await refresh(); go(itemRoute(brand, type, id)); }} />
    </>
  );
}

/**
 * A new item asks only for its name; its id comes from the name (it never changes and apps link
 * to it), its kind from the brand's vocabulary. It starts as a draft; a title's video is added in
 * its form (a draft may be saved before it has one).
 */
export function NewItem({ open, type, taken, onClose, onCreated }: { open: boolean; type: EntityType; taken: Set<string>; onClose: () => void; onCreated: (id: string) => Promise<void> }) {
  const { client, pack } = useStudio();
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const one = lower(noun(pack, type));
  const id = uniqueId(name, taken);

  useEffect(() => { if (open) { setName(""); setError(""); } }, [open]);

  const create = async () => {
    if (!name.trim()) { setError(`Give the ${one} a name.`); return; }
    setBusy(true); setError("");
    try {
      await entities.create(client, type, id, blankEntity(type, id, client.brand.locales.default, name.trim(), pack.defaultTitleKind), "draft");
      await onCreated(id);
    } catch (e) {
      setError(e instanceof ApiError ? (e.code === "already_exists" ? "Something with this name already exists. Try another name." : ((e.body["issues"] as string[] | undefined)?.join("; ") ?? "It was not created.")) : "It was not created. Check your connection.");
    } finally { setBusy(false); }
  };

  return (
    <Modal open={open} onClose={onClose} size="sm">
      <form className="flex flex-col gap-4" onSubmit={(e) => { e.preventDefault(); void create(); }} noValidate>
        <h2 className="text-h5">New {one}</h2>
        <Field label="Name" value={name} error={error && !name.trim() ? error : undefined} autoFocus onChange={(e) => setName(e.target.value)}
          description={name.trim() ? `It starts as a draft. Its link name will be “${id}”.` : type === "title" ? "It starts as a draft. Add its video, artwork and the rest next." : "It starts as a draft: nothing changes in the app yet."} />
        {error && name.trim() ? <Notice tone="critical">{error}</Notice> : null}
        <div className="flex flex-wrap gap-2">
          <Button variant="primary" type="submit" isPending={busy}>Create draft</Button>
          <Button variant="outline" type="button" onClick={onClose}>Cancel</Button>
        </div>
      </form>
    </Modal>
  );
}
